import { useAccreditationStore } from "@/store/accreditation";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpRightSquare,
  ArrowUpSquare,
  Box,
  BoxIcon,
  CheckCircle,
  ChevronRightIcon,
  Clock,
  Command,
  CornerDownLeftIcon,
  ExternalLink,
  Mail,
  Phone,
  Send,
  Shield,
  User,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery } from "react-query";
import debounce from "lodash/debounce";
import { BASE_URL } from "@/dashboardQueries";
import axios from "axios";
import { ButtonSpinner } from "@/components/ButtonSpinner";
import {
  AccreditationGroup,
  AccreditationIndividual,
  TicketAllocation,
} from "@/models/accreditation";
import { Link, useNavigate } from "react-router-dom";
import { TicketBadge } from "./TicketBadge";
import { TicketIssueStatus } from "@/constants/accreditation";

export const GlobalSearch = () => {
  const { searchOpen, setSearchOpen } = useAccreditationStore();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  // --- Debounced search state ---
  const [searchValue, setSearchValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce input
  const debouncedSetQuery = useCallback(
    debounce((val: string) => setDebouncedQuery(val), 300),
    []
  );

  useEffect(() => {
    debouncedSetQuery(searchValue);
  }, [searchValue, debouncedSetQuery]);

  // React Query for search
  const {
    data: searchResults,
    isLoading: isSearchLoading,
    isError: isSearchError,
  } = useQuery(
    ["accreditationSearch", debouncedQuery],
    async () => {
      const response = await axios.get(
        `${BASE_URL}/accreditation/search?query=${encodeURIComponent(
          debouncedQuery
        )}`
      );
      return response.data;
    },
    {
      enabled: debouncedQuery.length >= 3,
      keepPreviousData: true,
    }
  );

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const resultRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Detect platform for modifier key
  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;

  // Keyboard navigation and close on Escape
  useEffect(() => {
    if (!searchOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSearchOpen(false);
        setSearchValue(""); // Clear search after closing
      }
      if (e.key === "ArrowDown") {
        setSelectedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        e.preventDefault();
      }
      if (e.key === "ArrowUp") {
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        e.preventDefault();
      }
      // Enter for open
      if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) {
        if (searchResults[selectedIndex]) {
          const name =
            searchResults[selectedIndex].type === "individual"
              ? `${searchResults[selectedIndex].firstName} ${searchResults[selectedIndex].lastName}`
              : searchResults[selectedIndex].name;

          setSearchOpen(false);
          // Open depending on the type
          navigate(
            `/dashboard/accreditation/${searchResults[selectedIndex].type}s/${searchResults[selectedIndex].id}`
          );
          setSearchValue(""); // Clear search input after navigating
        }
      }
      // CMD/CTRL + Enter for open in new tab
      if (
        e.key === "Enter" &&
        ((isMac && e.metaKey) || (!isMac && e.ctrlKey))
      ) {
        if (searchResults[selectedIndex]) {
          const name =
            searchResults[selectedIndex].type === "individual"
              ? `${searchResults[selectedIndex].firstName} ${searchResults[selectedIndex].lastName}`
              : searchResults[selectedIndex].name;

          // Open in new tab depending on the type
          window.open(
            `/dashboard/accreditation/${searchResults[selectedIndex].type}s/${searchResults[selectedIndex].id}`,
            "_blank"
          );
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchOpen, setSearchOpen, searchResults, selectedIndex, isMac]);

  // Close on click outside
  useEffect(() => {
    if (!searchOpen) return;
    const handleClick = (e: MouseEvent) => {
      // Only close if the click is outside the modal (not inside)
      if (
        containerRef.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        setSearchOpen(false);
        setSearchValue(""); // Clear search input after navigating
      }
    };
    // Use 'mousedown' for better UX, but ignore if the click is inside the modal
    document.addEventListener("mousedown", handleClick, true);
    return () => document.removeEventListener("mousedown", handleClick, true);
  }, [searchOpen, setSearchOpen]);

  // Use searchResults if available, otherwise fallback to RESULTS
  const displayResults =
    debouncedQuery.length >= 3 && searchResults ? searchResults : [];

  // Reset selectedIndex when search is opened/closed or results change
  useEffect(() => {
    if (searchOpen) setSelectedIndex(0);
  }, [searchOpen, displayResults.length]);

  // Scroll selected result into view when selectedIndex changes
  useEffect(() => {
    if (resultRefs.current[selectedIndex]) {
      resultRefs.current[selectedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  const selected = displayResults[selectedIndex];

  // Fetch detail for selected item
  const {
    data: selectedDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useQuery(
    selected &&
      selected.type &&
      (selected.id || selected.email || selected.name)
      ? [
          selected.type === "individual"
            ? "accreditationIndividualDetail"
            : "accreditationGroupDetail",
          selected.id || selected.email || selected.name,
        ]
      : [],
    async () => {
      if (!selected) return null;
      if (selected.type === "individual") {
        const id = selected.id || selected.email;
        if (!id) return null;
        const res = await axios.get(
          `${BASE_URL}/accreditation/individuals/${id}`
        );
        return res.data;
      } else if (selected.type === "group") {
        const id = selected.id || selected.name;
        if (!id) return null;
        const res = await axios.get(`${BASE_URL}/accreditation/groups/${id}`);
        return res.data;
      }
      return null;
    },
    {
      enabled:
        !!selected &&
        !!selected.type &&
        !!(selected.id || selected.email || selected.name),
      staleTime: 60 * 1000,
    }
  );

  const Icon = ({ result }: any) => {
    return (
      <div className="">
        {result.type === "individual" ? (
          <div className="bg-zinc-700 rounded-md p-1">
            <User className="h-3 w-3 text-white" />
          </div>
        ) : result.icon ? (
          <span className="text-white mr-0.5">{result.icon}</span>
        ) : (
          <div className="bg-zinc-700 rounded-md p-1">
            <Box className="h-3 w-3 text-white" />
          </div>
        )}
      </div>
    );
  };

  const TypeBadge = ({ result }: any) => {
    return (
      <div className="text-xs flex items-center bg-sky-800 px-1 border border-sky-600 rounded-md">
        {result.type === "individual" ? (
          <User className="h-3 w-3 text-white mr-1" />
        ) : (
          <Box className="h-3 w-3 text-white mr-1" />
        )}
        {result.type === "individual" ? "Individual" : "Group"}
      </div>
    );
  };

  // Global "/" key handler to open search
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only open if not already open, and not typing in an input/textarea/select
      if (
        e.key === "/" &&
        !searchOpen &&
        !(
          (e.target instanceof HTMLInputElement && !e.target.readOnly) ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement ||
          (e.target as HTMLElement)?.isContentEditable
        )
      ) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [searchOpen, setSearchOpen]);

  const IndividualDetails = ({
    individual,
  }: {
    individual: AccreditationIndividual;
  }) => {
    const individualHasPaymentLink = individual?.tickets?.some(
      (ticket) => ticket.order
    );

    return (
      <>
        <div className="mt-3 flex items-center text-zinc-600">
          <Mail className="inline h-4 w-4 mr-2" />
          <p className="text-sm dark:text-zinc-400">{individual.email}</p>
        </div>

        {individual.phoneNumber != "False" && individual.phoneNumber && (
          <div className="mt-3 flex items-center text-zinc-600">
            <Phone className="inline h-3 w-3 mr-1" />
            <p className="text-sm dark:text-zinc-400">
              {individual.phoneNumber}
            </p>
          </div>
        )}

        <Link
          to={`/dashboard/accreditation/groups/${individual.group.id}`}
          className="text-sm mt-3 flex items-center cursor-pointer dark:hover:text-zinc-600 text-zinc-600 transition group"
        >
          <Icon result={individual.group} />
          <p className="ml-1.5 dark:text-zinc-400">{individual.group.name}</p>
          <ExternalLink className="inline h-3 w-3 ml-1" />
        </Link>

        <div className="mt-3">
          {individual.tickets.length > 0 ? (
            individual.tickets.map((ticket, index) => (
              <div key={index} className="mb-2">
                <div className="flex">
                  <TicketBadge
                    ticket={ticket}
                    groupId={individual.group.id}
                    individualId={individual.id}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="mt-3 py-4 mb-4 border dark:border-white/10 border-dashed rounded-md flex items-center justify-center px-4">
              <p className="dark:text-gray-400 text-sm text-center text-zinc-600">
                This individual has no tickets
              </p>
            </div>
          )}
        </div>

        <div className="mt-3 mb-4">
          {individualHasPaymentLink && individual?.tickets[0]?.order && (
            <>
              {individual?.tickets[0].status === TicketIssueStatus.PENDING ? (
                <span
                  id="payment-status-badge"
                  className="inline-flex items-center rounded-md bg-yellow-400/10 px-2 py-1 text-xs font-medium text-yellow-500 ring-1 ring-inset ring-yellow-400/20"
                >
                  Payment Pending
                  <Clock className="ml-1.5 h-3.5 w-3.5 text-yellow-500" />
                </span>
              ) : (
                <span
                  id="payment-status-badge"
                  className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-500 ring-1 ring-inset ring-green-400/20"
                >
                  Paid
                  <CheckCircle className="ml-1.5 h-3.5 w-3.5 text-green-500" />
                </span>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  const GroupDetails = ({ group }: { group: AccreditationGroup }) => {
    return (
      <div className="mb-4">
        <div>
          <ol role="list" className="flex items-center space-x-4 mt-2">
            {group?.hierarchy.map((item, index) => (
              <li key={index}>
                <div className="flex items-center">
                  {index > 0 && (
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="size-5 shrink-0 text-gray-500"
                    />
                  )}
                  <Link
                    to={`/dashboard/accreditation/groups/${item.id}`}
                    className={`text-sm font-medium dark:hover:text-white ${
                      index === group.hierarchy.length - 1
                        ? "text-gray-800 dark:text-white hover:text-gray-600 transition duration-200"
                        : "dark:text-gray-300 text-gray-500"
                    } ${index > 0 ? "ml-2" : ""}`}
                  >
                    {item.name}
                  </Link>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="mt-3">
          <p className="text-xs dark:text-zinc-400 text-zinc-600 font-medium">
            Group contacts
          </p>

          {group.groupContacts && group.groupContacts.length > 0 ? (
            <ul className="mt-1">
              {group.groupContacts.map((contact, index) => (
                <li
                  key={index}
                  className="text-xs flex items-center text-sm dark:text-zinc-400 text-white mb-1 bg-zinc-800 border dark:border-white/20 rounded-lg px-2 py-0.5 inline-flex"
                >
                  <Mail className="h-3 w-3 text-zinc-400 mr-1" />
                  {contact.email}
                </li>
              ))}
            </ul>
          ) : (
            <p className="dark:text-zinc-200 text-xs mt-1">No group contacts</p>
          )}

          <div className="mt-3">
            <p className="text-xs dark:text-zinc-400 text-zinc-600 font-medium mb-1">
              Tickets
            </p>

            {group.groupAllocatedTickets.length > 0 ? (
              group.groupAllocatedTickets.map(
                (ticketAllocation: TicketAllocation) => (
                  <span className="mr-1 text-xs flex items-center text-sm dark:text-zinc-400 text-white mb-1 bg-zinc-800 border dark:border-white/20 rounded-lg px-2 py-0.5 inline-flex">
                    {ticketAllocation.claimed}/{ticketAllocation.quantity}{" "}
                    {ticketAllocation.ticket.name}
                  </span>
                )
              )
            ) : (
              <p className="dark:text-zinc-200 text-xs mt-1">
                No tickets allocated to this group
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleItemClick = (result: any) => {
    setSearchOpen(false);
    // Navigate to the appropriate page based on the type
    navigate(`/dashboard/accreditation/${result.type}s/${result.id}`);
    setSearchValue(""); // Clear search input after navigating
  };

  return (
    <AnimatePresence>
      {searchOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-black/20 backdrop-blur-sm fixed inset-0 z-50 flex justify-center items-center"
        >
          <motion.div
            ref={containerRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="p-[1px] dark:bg-zinc-900 bg-gray-50 max-w-3xl w-full lg:-mt-44 rounded-lg border dark:border-white/10 shadow-lg"
          >
            {/* attio look (add below): border border-white/10 rounded-md */}
            <div className="">
              <div className="mx-3 border-b py-1 dark:border-white/10">
                <input
                  autoFocus
                  placeholder="Search...."
                  className="w-full border-none focus:ring-0 bg-transparent dark:text-white text-zinc-600"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>

              <div className="max-h-96 flex overflow-y-scroll">
                <div className="w-1/2 px-4 pt-2 border-r dark:border-white/10 pb-2 overflow-y-scroll">
                  {displayResults.length > 0 && (
                    <div className="text-sm px-1 font-medium text-zinc-500 mt-1">
                      Results
                    </div>
                  )}
                  <div className="mt-2">
                    {isSearchLoading && debouncedQuery.length >= 3 ? (
                      <div className="text-zinc-400 text-sm flex flex items-center">
                        <ButtonSpinner />
                        <p>Searching...</p>
                      </div>
                    ) : isSearchError && debouncedQuery.length >= 3 ? (
                      <div className="text-red-400 text-sm">
                        Error searching.
                      </div>
                    ) : displayResults.length === 0 &&
                      debouncedQuery.length < 3 ? (
                      <div className="text-zinc-400 text-sm pb-4">
                        Type at least 3 characters to search
                      </div>
                    ) : displayResults.length === 0 &&
                      debouncedQuery.length >= 3 ? (
                      <div className="text-zinc-400 text-sm pb-4">
                        No results found.
                      </div>
                    ) : (
                      <ul>
                        {displayResults.map((result: any, index: number) => (
                          <li
                            key={index}
                            ref={(el) => (resultRefs.current[index] = el)}
                            className={`flex justify-between dark:hover:bg-zinc-800 cursor-pointer p-1 rounded-lg transition mb-1 ${
                              selectedIndex === index
                                ? "dark:bg-zinc-800 bg-zinc-200 ring-2 ring-blue-500"
                                : ""
                            }`}
                            onClick={() => handleItemClick(result)}
                            tabIndex={0}
                          >
                            <div className="flex flex-1 items-center w-3/4">
                              <div className="mr-2">
                                {result && <Icon result={result} />}
                              </div>
                              <div className="text-sm text-nowrap truncate pr-2 dark:text-white text-zinc-600">
                                {result.name}{" "}
                                {result.type === "individual" &&
                                  result.email && (
                                    <span className="text-xs text-zinc-400 truncate ml-1">
                                      {result.email}
                                    </span>
                                  )}
                              </div>
                            </div>
                            <div className="flex items-center">
                              {result && <TypeBadge result={result} />}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="w-1/2 pt-3 px-4 overflow-y-scroll">
                  {selected && (
                    <>
                      <div className="border-b dark:border-white/10 pb-2 flex justify-between items-center">
                        {/* Show details for selected result */}
                        <div className="flex">
                          <div className="mr-2">
                            <Icon result={selected} />
                          </div>
                          <span className="text-sm dark:text-white text-zinc-600">
                            {selected?.type === "individual"
                              ? `${selected?.name}`
                              : selected?.name}
                          </span>

                          <div className="flex by-red-500 ml-2 -mt-0.5">
                            {selectedDetail?.isGroupContact && (
                              <div className="has-tooltip relative">
                                <span className="inline-flex items-center rounded-md bg-blue-400/10 px-1.5 py-1 text-xs font-medium text-blue-400 ring-1 ring-inset ring-blue-400/30 mr-1">
                                  <Shield className="h-3 w-3" />
                                </span>
                                <div className="z-50 tooltip rounded shadow-lg px-2 py-1 -mb-8 -ml-3 bottom-0 -translate-x-1/4 text-nowrap bg-dark-secondary text-xs text-gray-400 border border-white/10 font-light">
                                  Group contact
                                </div>
                              </div>
                            )}
                            {selectedDetail?.invitationSent && (
                              <div className="has-tooltip relative">
                                <span className="inline-flex items-center rounded-md bg-yellow-500/10 px-1.5 py-1 text-xs font-medium text-yellow-400 ring-1 ring-inset ring-yellow-500/20 mr-1.5">
                                  <Send className="h-3 w-3" />
                                </span>
                                <div className="z-50 tooltip rounded shadow-lg px-2 py-1 -mb-8 -ml-3 bottom-0 -translate-x-1/4 text-nowrap bg-dark-secondary text-xs text-gray-400 border border-white/10 font-light">
                                  Invitation sent
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>{selected && <TypeBadge result={selected} />}</div>
                      </div>
                      <div>
                        {isDetailLoading ? (
                          <div className="text-zinc-400 text-sm mt-4 flex items-center">
                            <ButtonSpinner />
                            <p>Loading details...</p>
                          </div>
                        ) : isDetailError ? (
                          <div className="text-red-400 text-sm mt-4">
                            Error loading details.
                          </div>
                        ) : selectedDetail ? (
                          <>
                            {selected.type === "group" ? (
                              <GroupDetails group={selectedDetail} />
                            ) : (
                              <IndividualDetails individual={selectedDetail} />
                            )}
                          </>
                        ) : (
                          <div className="text-zinc-400 text-sm mt-4">
                            No details found.
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="border-t dark:border-white/10 py-3 px-4 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="flex">
                    <div className="border h-full p-0.5 mr-0.5 rounded-lg dark:border-white/10">
                      <ArrowUp className="h-4 w-4 text-zinc-500" />
                    </div>
                    <div className="border p-0.5 rounded-lg dark:border-white/10">
                      <ArrowDown className="h-4 w-4 text-zinc-500" />
                    </div>
                  </div>
                  <div className="ml-2 text-sm text-zinc-500">Navigate</div>
                </div>
                <div className="flex -mr-1">
                  {displayResults[selectedIndex] && (
                    <>
                      <button
                        className="mr-2 dark:text-white dark:ring-white/10 flex justify-center rounded-md dark:bg-zinc-800 bg-white px-3 py-2 text-sm font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 dark:hover:bg-zinc-800 hover:bg-gray-100"
                        onClick={() => {
                          const name =
                            displayResults[selectedIndex].type === "individual"
                              ? `${displayResults[selectedIndex].firstName} ${displayResults[selectedIndex].lastName}`
                              : displayResults[selectedIndex].name;
                        }}
                      >
                        Open
                        <div className="border border-white/10 p-0.5 rounded-md ml-2">
                          <CornerDownLeftIcon className="h-3.5 w-3.5 text-zinc-400" />
                        </div>
                      </button>
                      <button
                        className="dark:text-white dark:ring-white/10 flex justify-center rounded-md dark:bg-zinc-800 bg-white px-3 py-2 text-sm font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 dark:hover:bg-zinc-800 hover:bg-gray-100"
                        onClick={() => {
                          const name =
                            displayResults[selectedIndex].type === "individual"
                              ? `${displayResults[selectedIndex].firstName} ${displayResults[selectedIndex].lastName}`
                              : displayResults[selectedIndex].name;
                        }}
                      >
                        Open in new tab
                        <div className="border border-white/10 p-0.5 rounded-md ml-2">
                          {isMac ? (
                            <Command className="h-3.5 w-3.5 text-zinc-400" />
                          ) : (
                            "CTRL"
                          )}
                        </div>
                        <div className="border border-white/10 p-0.5 rounded-md ml-1">
                          <CornerDownLeftIcon className="h-3.5 w-3.5 text-zinc-400" />
                        </div>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
